// One-time migration: copy pre-existing IndexedDB data to the server.
//
// Before persistence moved server-side, projects/models/routes/missions
// lived only in IndexedDB. This pushes that data to the server once so a
// returning user keeps their work. Model BINARY (GLB blobs, LAS buffers)
// stays in IndexedDB — only metadata is sent. Idempotent + flag-guarded,
// so it's a no-op on every load after the first successful run.

import * as local from './idbLocal.js';

const FLAG = 'twin-migrated-to-server-v1';

const BINARY_KEYS = new Set([
  'blob', 'url', 'positionsBuf', 'colorsBuf',
  'meshPositionsBuf', 'meshColorsBuf', 'meshIndicesBuf',
  '_pointPos', '_pointCol', '_providerRegistered',
]);

function stripBinary(model) {
  const meta = {};
  for (const [k, v] of Object.entries(model)) {
    if (BINARY_KEYS.has(k) || k.startsWith('_')) continue;
    meta[k] = v;
  }
  return meta;
}

async function put(url, body) {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PUT ${url} → ${res.status}`);
}

// Memoized singleton — every caller awaits the same run, so the projects
// slice can `await ensureMigrated()` without racing the startup kick.
let _migrationPromise = null;
export function ensureMigrated() {
  if (!_migrationPromise) _migrationPromise = migrateLocalToServerOnce();
  return _migrationPromise;
}

export async function migrateLocalToServerOnce() {
  try {
    if (localStorage.getItem(FLAG)) return { skipped: true };
  } catch {
    return { skipped: true };
  }

  let projects = [];
  try {
    projects = await local.getAllProjects();
  } catch {
    // No local DB / blocked — nothing to migrate; mark done so we don't retry.
    try { localStorage.setItem(FLAG, String(Date.now())); } catch { /* */ }
    return { skipped: true, reason: 'no local db' };
  }

  if (!projects.length) {
    try { localStorage.setItem(FLAG, String(Date.now())); } catch { /* */ }
    return { migrated: 0 };
  }

  const counts = { projects: 0, models: 0, routes: 0, missions: 0 };
  try {
    for (const p of projects) {
      await put(`/api/projects/${p.id}`, p);
      counts.projects++;

      const [models, routes, missions] = await Promise.all([
        local.getModelsForProject(p.id).catch(() => []),
        local.getRoutesForProject(p.id).catch(() => []),
        local.getMissionsForProject(p.id).catch(() => []),
      ]);
      for (const m of models) { await put(`/api/models/${m.id}`, stripBinary(m)); counts.models++; }
      // Legacy Alert Areas rows (alertArea / gate) are refused by the server
      // since 2026-10-08; they are not carried over.
      for (const r of routes) { if (r?.type === 'alertArea' || r?.type === 'gate') continue; await put(`/api/routes/${r.id}`, r); counts.routes++; }
      for (const ms of missions) { await put(`/api/missions/${ms.id}`, ms); counts.missions++; }
    }
    localStorage.setItem(FLAG, String(Date.now()));
    console.log('[twin-viewer] migrated IndexedDB → server:', counts);
    return { migrated: counts.projects, counts };
  } catch (e) {
    // Leave the flag UNSET so we retry next load (server may have been down).
    console.warn('[twin-viewer] migration incomplete, will retry next load:', e?.message || e);
    return { error: String(e?.message || e), counts };
  }
}
